import React from 'react';
import { X, Video, Phone, MessageSquare, Sparkles } from 'lucide-react';

const StartConsultingModal = ({ appointment, onSelectMode, onClose }) => {
  const consultationModes = [
    {
      mode: 'video',
      icon: Video,
      title: 'Video Call',
      description: 'Face-to-face consultation with video',
      color: 'from-emerald-500 to-green-600',
      hoverColor: 'from-emerald-600 to-green-700',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      mode: 'audio',
      icon: Phone,
      title: 'Audio Call',
      description: 'Voice-only consultation',
      color: 'from-blue-500 to-blue-600',
      hoverColor: 'from-blue-600 to-blue-700',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
    {
      mode: 'chat',
      icon: MessageSquare,
      title: 'Text Chat',
      description: 'Message-based consultation',
      color: 'from-purple-500 to-purple-600',
      hoverColor: 'from-purple-600 to-purple-700',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
    },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-green-600 p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-1">
                Start Consultation
              </h3>
              <p className="text-emerald-100 text-sm">
                Choose how you'd like to connect with {appointment.patient}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Appointment Info */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 mb-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Patient</p>
                <p className="font-bold text-gray-900 text-lg">{appointment.patient}</p>
                <p className="text-sm text-gray-600 mt-1">{appointment.type}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 mb-1">Scheduled</p>
                <p className="font-semibold text-gray-900">{appointment.time}</p>
              </div>
            </div>
          </div>

          {/* Mode Selection */}
          <div className="space-y-3">
            <p className="text-sm font-bold text-gray-900 mb-4">Select consultation mode</p>
            {consultationModes.map(({ mode, icon: Icon, title, description, color, hoverColor, iconBg, iconColor }) => (
              <button
                key={mode}
                onClick={() => onSelectMode(mode)}
                className={`w-full group relative overflow-hidden rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl`}
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                <div className="relative bg-white border-2 border-gray-200 group-hover:border-transparent rounded-2xl p-5 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={`${iconBg} group-hover:bg-white/20 p-4 rounded-xl transition-colors`}>
                      <Icon className={`w-7 h-7 ${iconColor} group-hover:text-white transition-colors`} />
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className="text-lg font-bold text-gray-900 group-hover:text-white mb-1 transition-colors">
                        {title}
                      </h4>
                      <p className="text-sm text-gray-600 group-hover:text-white/90 transition-colors">
                        {description}
                      </p>
                    </div>
                    <div className="text-gray-400 group-hover:text-white transition-colors">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Cancel Button */}
          <button
            onClick={onClose}
            className="w-full mt-6 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default StartConsultingModal;
